<template>
  <div class="view-edit-orders">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <master-order-selector
        v-if="masterOrderSelectorOrders.length > 1"
        :key="'master-order-selector-key' + refreshMasterOrderSelector"
        :orders="masterOrderSelectorOrders"
        :enable-add-button="false"
        :selected-order-is-valid="selectedOrderIsValid"
        :enable-edit-button="masterOrderSelectorAddButtonEnabled"
        :enable-clone-button="false"
        :enable-delete-button="false"
        v-on:edit-order="editOrder"
        v-on:select-order="selectOrder">
      </master-order-selector>

      <div v-if="!loadingRelatedOrder">
        <div
          v-if="selectedOrder && selectedOrder.orderNumber !== orderOverviewId"
          class="view-edit-order-details-container">
          <ViewEditOrderDetails
            :key="'details' + selectedOrder.orderNumber"
            :order="selectedOrder"
            :maximum-date="maximumDate"
            v-on:order-updated="updateCurrentOrder"
            v-on:order-valid="setSelectedOrderIsValid">
          </ViewEditOrderDetails>
        </div>

        <div v-if="isPackingSlipFetched" class="view-edit-order-container">
          <grid-order-shipments
            :key="'shipment' + selectedOrder.orderNumber"
            :order="selectedOrder"
            v-on:shipment-selected="selectShipmentDetails" />
        </div>

        <div class="view-edit-order-container" v-if="orderForItems">
          <div v-if="showShipmentSelector" class="item-shipment-details-selector">
            <select
              v-model="selectedShipmentDetails"
              class="with-arrow select-packingSlip"
              v-on:change="selectShipmentDetails">
              <option :value="selectedShipmentDetailsDefault">
                {{ $t('viewOrder.item_shipment_details_selector.all_items') }}
              </option>
              <option
                v-for="packingSlip in selectedOrder.packingSlips"
                :key="packingSlip.deliveryNumber"
                :value="packingSlip.deliveryNumber">
                {{ packingSlip.deliveryNumber }}
              </option>
            </select>
          </div>
          <grid-order-items
            :order="orderForItems"
            :selected-shipment="selectedShipmentDetails"
            :is-draft="isDraft"
            :key="'grid-order-items-key' + refreshGridOrderItems" />
        </div>

        <div v-if="isDraft" class="splits-cnt">
          <product-split
            :key="orderNumbersForSplitKey"
            :order-numbers="orderNumbersForSplit"
            :is-draft="isDraft"
            :collapsable="true"
            :show-error-message="false">
          </product-split>
        </div>

        <view-order-totals v-if="!isDraft" :key="selectedOrder.orderNumber" :order="selectedOrder"> </view-order-totals>

        <div v-if="!isDraft && isOrderInUpdateProgress(selectedOrder)" class="order-update-note">
          {{ $t('viewOrder.update_order_note') }}
        </div>

        <div v-if="isDraft" class="terms-and-conditions" v-html="$t('viewOrder.terms_and_conditions')"></div>
      </div>
      <div v-else class="loader"></div>
    </div>
  </div>
</template>

<script lang="ts">
import ProductSplit from '@/components/summary/product-split.vue';
import { orderOverviewId } from '@/constants/order.constants';
import { DraftModel, OrderType } from '@onrunning/backstage-models';
import structuredClone from '@ungap/structured-clone';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../../composables/format';
import { toRawDeep } from '../../composables/to-raw-deep';
import { ordersMixins } from '../../mixins/orders.mixins';
import { utilsMixins } from '../../mixins/utils.mixins';
import { Feature } from '../../models/feature.model';
import { MasterOrderSelectorModel } from '../../models/master-order-selector.model';
import { MatrixOrder } from '../../models/matrix-order.model';
import { OrderProduct } from '../../models/order-product.model';
import { Order } from '../../models/order.model';
import { ValidatorResult } from '../../models/validation-result.model';
import { RouteNames } from '../../router';
import { DraftsService } from '../../services/drafts.service';
import { FeaturesService } from '../../services/features.service';
import { OrdersService } from '../../services/orders.service';
import { useUserStore } from '../../stores/user';
import GridOrderItems, { ALL_ITEMS } from './grid-order-items.vue';
import GridOrderShipments from './grid-order-shipments.vue';
import MasterOrderSelector from './master-order-selector.vue';
import ViewEditOrderDetails from './view-edit-order-details.vue';
import ViewOrderTotals from './view-order-totals.vue';

export default defineComponent({
  name: 'view-edit-orders',
  components: {
    'grid-order-shipments': GridOrderShipments,
    'grid-order-items': GridOrderItems,
    'master-order-selector': MasterOrderSelector,
    ViewEditOrderDetails,
    'view-order-totals': ViewOrderTotals,
    'product-split': ProductSplit,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    orderItems: {
      type: Array<Order | DraftModel>,
      default: undefined,
    },
    orderIsDraft: {
      type: Boolean,
      default: false,
    },
    maximumDate: {
      type: Date,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { formatDateToIsoStringIgnoringTime } = useFormat();

    return {
      account: selectedAccount,
      formatDateToIsoStringIgnoringTime,
    };
  },
  data() {
    return {
      loading: true,
      loadingRelatedOrder: false,
      submitting: false,
      saving: false,
      deleting: false,
      refreshMasterOrderSelector: 0,
      refreshGridOrderItems: 0,
      orders: new Array<Order>(),
      orderForItems: new Order(),
      isDraft: false,
      draftsService: new DraftsService(),
      ordersService: new OrdersService(),
      featuresService: new FeaturesService(),
      salesOrdersDisabled: true,
      isPackingSlipFetched: false,
      orderEditValid: true,
      selectedShipmentDetails: ALL_ITEMS,
      selectedShipmentDetailsDefault: ALL_ITEMS,
      masterOrderSelectorAddButtonEnabled: false,
      orderOverviewId: orderOverviewId,
      showRelatedOrders: this.$router.currentRoute.value.query.showRelatedOrders === 'true',
    };
  },
  computed: {
    selectedOrder(): Order {
      return this.orders.find((order) => order.selected) ?? this.orders[0];
    },
    orderNumbersForSplit(): string[] {
      if (this.selectedOrder.orderNumber === this.orderOverviewId) {
        return this.orders
          .filter((order) => order.orderNumber !== this.orderOverviewId)
          .map((order) => order.orderNumber);
      }

      return [this.selectedOrder.orderNumber];
    },
    orderNumbersForSplitKey(): string {
      if (this.selectedOrder.orderNumber === this.orderOverviewId) {
        return this.orderOverviewId;
      }

      return this.selectedOrder.orderNumber;
    },
    selectedOrderIndex(): number {
      return this.orders.findIndex((order) => order.selected) ?? 0;
    },
    showShipmentSelector(): boolean {
      return !!this.selectedOrder?.packingSlips?.length && this.selectedOrder?.packingSlips?.length > 0;
    },
    masterOrderSelectorOrders(): MasterOrderSelectorModel[] {
      const selectorRelatedOrders = this.orders.map<MasterOrderSelectorModel>((order) => ({
        id: order.orderNumber,
        title: order.dueDate
          ? this.formatDateToIsoStringIgnoringTime(order.dueDate)
          : (this.$t('viewOrder.overview') as string),
        subTitle:
          !this.orderIsDraft && order.orderNumber !== this.orderOverviewId
            ? `${order.orderNumber}`
            : `${order.products
                .reduce((totalQuantities, product) => totalQuantities + product.quantity, 0)
                .toString()} ${this.$t('viewOrder.items')}`,
        selected: order.selected ?? false,
      }));

      this.orders
        .filter((order) => !!order.relatedOrders)
        .flatMap((order) => order.relatedOrders)
        .forEach((relatedOrder) => {
          if (
            relatedOrder &&
            !selectorRelatedOrders.some((selectorRelatedOrder) => selectorRelatedOrder.id === relatedOrder.orderNumber)
          ) {
            selectorRelatedOrders.push({
              id: relatedOrder.id,
              title: relatedOrder.requestedDate,
              subTitle: relatedOrder.orderNumber,
              selected: false,
            });
          }
        });

      return selectorRelatedOrders;
    },
    selectedOrderIsValid(): boolean {
      return this.orderEditValid;
    },
    selectedOrderDate(): Date {
      return this.selectedOrder.date as Date;
    },
  },
  mounted() {
    this.loadOrders();
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    goBackToOrders() {
      this.$router.push('/orders');
    },
    async determineWhetherSalesOrdersAreDisabled() {
      this.salesOrdersDisabled = await this.featuresService.hasFeature(Feature.SALES_ORDERS_DISABLED, this.account.id);
    },
    selectShipmentDetails(input: Event | string) {
      const deliveryNumber = typeof input === 'string' ? input : (input.target as HTMLInputElement).value || '';

      this.selectedShipmentDetails = deliveryNumber;
    },
    showSuccessNotification() {
      this.$snotify.success(this.$t('viewOrder.submit_order_success'));
    },
    updateCurrentOrder(updatedOrder: Order) {
      if (moment.isDate(updatedOrder.dueDate) && this.selectedOrder.dueDate !== updatedOrder.dueDate) {
        this.refreshMasterOrderSelector++;
        this.selectedOrder.dueDate = updatedOrder.dueDate;
        const currentOrder = this.orders.find((order) => order.id === updatedOrder.id);

        if (currentOrder) {
          currentOrder.dueDate = updatedOrder.dueDate;
        }
      }

      this.selectedOrder.deliveryAddress = updatedOrder.deliveryAddress;
      this.selectedOrder.contactId = updatedOrder.contactId;
      this.selectedOrder.customerPoNumber = updatedOrder.customerPoNumber;

      this.$emit(
        'update-orders',
        this.orders.filter((order: Order) => order.id !== this.orderOverviewId),
      );
    },
    setSelectedOrderIsValid(valid: boolean) {
      this.orderEditValid = valid;
      // ToDo use provide / inject on view-edit-order-details order-valid event to avoid this prop drilling
      this.$emit('orders-are-valid', valid);
    },
    addNewOrder() {
      this.$emit('add-new-order', { selected: true } as Partial<MatrixOrder>);
    },
    async selectOrder(selectedIndex: number) {
      const orderIndex = this.orders.findIndex((order, index) => index === selectedIndex);

      if (orderIndex !== selectedIndex) {
        this.loadingRelatedOrder = true;
        const selectedId = this.masterOrderSelectorOrders[selectedIndex].id;

        let result: ValidatorResult<Order | DraftModel>;

        if (this.orderIsDraft) {
          result = await this.draftsService.get(selectedId, this.account.id);
        } else {
          result = await this.ordersService.get(selectedId, this.account.id);
        }

        if (result.model) {
          this.orders[selectedIndex] = result.model as Order;
        } else {
          // ToDo show error popup
          return;
        }
        this.loadingRelatedOrder = false;
      }

      this.orders = this.orders.map((order, index) => ({
        ...order,
        selected: index === selectedIndex,
      }));

      this.orderForItems = Object.assign(new Order(), structuredClone(toRawDeep(this.selectedOrder)));
      this.refreshGridOrderItems++;

      this.$emit(
        'update-orders',
        this.orders.filter((order) => order.id !== this.orderOverviewId),
      );
    },
    editOrder(index: number) {
      this.$emit('edit-order-quantities', this.orders[index].id);
    },
    configureMasterOderView() {
      this.orders = this.orders.filter((order) => order.orderNumber !== this.orderOverviewId);

      let orderOverview: Order | undefined;

      if (this.orders.length > 1) {
        const { accumulatedOrderOverview, orderOverviewTotal } = this.orders.reduce(
          ({ accumulatedOrderOverview, orderOverviewTotal }, order: Order) => {
            accumulatedOrderOverview.products = (structuredClone(toRawDeep(order.products)) as OrderProduct[]).reduce(
              (overviewProducts, product) => {
                const currentOverviewProduct = accumulatedOrderOverview.products?.find(
                  (overviewProduct) => overviewProduct.productId === product.productId,
                );

                if (currentOverviewProduct) {
                  currentOverviewProduct.quantity += product.quantity;
                } else {
                  overviewProducts.push(product);
                }

                return overviewProducts;
              },
              accumulatedOrderOverview.products ?? [],
            );

            return { accumulatedOrderOverview, orderOverviewTotal: orderOverviewTotal + (order?.total ?? 0) };
          },
          { accumulatedOrderOverview: new Order(), orderOverviewTotal: 0 },
        );

        accumulatedOrderOverview.orderNumber = this.orderOverviewId;
        accumulatedOrderOverview.currencyCode = this.orders[0].currencyCode;
        accumulatedOrderOverview.total = orderOverviewTotal;
        orderOverview = accumulatedOrderOverview;
      }
      // Should we support overview for view order page?

      if (orderOverview) {
        this.orders = [orderOverview].concat(this.orders);
      }

      this.masterOrderSelectorAddButtonEnabled = this.isDraft;
    },
    async loadOrders() {
      if (!this.account) {
        return this.$router.push({ name: RouteNames.Home });
      }

      await this.determineWhetherSalesOrdersAreDisabled();

      if (this.orderItems && this.orderItems.length) {
        this.orders = this.orderItems as Order[];
      } else if (this.orderIsDraft && this.id) {
        const draftId = this.id;

        const draftValidation = await this.draftsService.get(draftId, this.account.id, this.showRelatedOrders);

        if (draftValidation.model) {
          this.orders = Object.assign(new Array<Order>(), [draftValidation.model]);
        } else {
          // ToDo show error
          this.goBackToOrders();

          return;
        }
      } else if (this.id) {
        const orderValidation = await this.ordersService.get(this.id, this.account.id, this.showRelatedOrders);
        if (orderValidation.model) {
          this.orders = Object.assign(new Array<Order>(), [orderValidation.model]);
          const isMultipleOrder = this.orders[0].type === OrderType.MultiplePreOrder;
          const hasRelatedOrders = this.orders[0].relatedOrders && this.orders[0].relatedOrders?.length > 0;

          if (isMultipleOrder && hasRelatedOrders) {
            await this.loadRelatedOrders();
          }

          this.isPackingSlipFetched = true;
        } else {
          // ToDo show error
          this.goBackToOrders();

          return;
        }
      }

      this.isDraft = this.theOrderIsDraft(this.orders[0]);

      if (this.orders.length > 1 || (this.orders[0].relatedOrders && this.orders[0].relatedOrders?.length > 0)) {
        this.configureMasterOderView();
      }

      if (!this.orders.find((order) => order.selected)) {
        this.selectOrder(0);
      } else {
        this.orderForItems = Object.assign(new Order(), structuredClone(toRawDeep(this.selectedOrder)));
      }

      this.loading = false;
    },
    async loadRelatedOrders() {
      const mainOrder = this.orders[0];
      const responses = await Promise.all(
        (mainOrder?.relatedOrders ?? [])
          .filter((relatedOrder) => relatedOrder.orderNumber !== mainOrder.orderNumber)
          .map((relatedOrder) => this.ordersService.get(relatedOrder.orderNumber, this.account.id)),
      );

      if (responses.every((response) => response.isValid())) {
        responses.forEach((orderValidation) => {
          if (orderValidation.model) {
            (this.orders as Order[]).push(orderValidation.model);
          }
        });
      }
    },
  },
});
</script>
<style scoped lang="scss">
.view-edit-orders {
  .view-edit-order-container {
    @include container-background;
    margin-bottom: 40px;
    position: relative;

    .item-shipment-details-selector {
      position: absolute;
      right: 20px;
    }
  }

  .view-edit-order-details-container {
    margin-bottom: 40px;
  }

  .select-packingSlip {
    position: relative;
    right: 0;
    margin: auto;
    margin-right: 0;
  }

  .order-update-note {
    font-size: 0.9em;
    margin-top: 20px;
    float: right;
    max-width: inherit;
    text-align: right;
  }

  .terms-and-conditions {
    padding: 60px 0 0;
    line-height: 150%;
    font-size: 14px;
  }

  .splits-cnt {
    padding-top: 40px;
  }
}
</style>
